import * as React from 'react';
import { memo } from 'react';

import { sliceContent } from '@restworld/utils-common';

import { TitleProps, FontSize, TextColor, FontWeightType } from './title.types';
import { titleIconMap, colorMap, sizeMap, fontWeightMap } from './title.constants';
import {
  IconWrapper,
  TitleContent,
  TitleWrapper,
  standardVariantToHtmlElement,
  variantToHtmlElement,
} from './title.styled-components';

/**
 * A component that renders a title with an optional icon and suffix icon.
 *
 * @param {string | React.ReactNode} content - The content of the title. Can be a string or a React node.
 * @param {FontSize} fontSize - The font size of the title. Defaults to 'base'.
 * @param {TextColor} textColor - The color of the title. Defaults to 'black'.
 * @param {FontWeightType} fontWeight - The font weight of the title. Defaults to 'normal'.
 * @param {string} icon - The name of the icon to display before the title.
 * @param {boolean} isCenter - Whether to center the title. Defaults to false.
 * @param {string} suffixIcon - The name of the icon to display after the title.
 * @param {number} maxChars - The maximum number of characters to display in the title. If the content is longer than this, it will be truncated. Defaults to undefined.
 * @param {React.ReactNode} children - Additional content to display inside the title.
 * @param {boolean} isPointer - Whether the title should have a pointer cursor. Defaults to false.
 * @param {string} twStyle - Tailwind CSS styles to apply to the title.
 * @param {string} variant - The variant of the title. Defaults to undefined.
 */
export const Title = memo(
  ({
    content,
    fontSize = 'base',
    textColor = 'black',
    fontWeight = 'normal',
    icon,
    isCenter = false,
    suffixIcon,
    maxChars,
    children,
    isPointer,
    twStyle,
    variant,
    wrapperStyle,
    desktopOnly,
    mobileOnly,
    align,
    standardVariant,
    testId,
    customPrefixIcon,
    customSuffixIcon,
    as,
    className,
  }: TitleProps) => (
    <TitleWrapper
      {...{ fontSize, textColor, isCenter, wrapperStyle, mobileOnly, desktopOnly }}
      className={className}
      as={as || standardVariantToHtmlElement(standardVariant) || variantToHtmlElement(variant)}
      data-cy={testId}
    >
      {icon && <IconWrapper>{titleIconMap[icon]}</IconWrapper>}
      {customPrefixIcon && <IconWrapper>{customPrefixIcon}</IconWrapper>}
      <TitleContent
        {...{
          fontWeight,
          isPointer,
          twStyle,
          variant,
          align,
          standardVariant,
        }}
        data-cy="title-content"
      >
        {children
          ? children
          : !!content && typeof content === 'string' && maxChars
          ? sliceContent(content, maxChars)
          : content}
      </TitleContent>
      {suffixIcon ? <IconWrapper>{titleIconMap[suffixIcon]}</IconWrapper> : null}
      {customSuffixIcon ? <IconWrapper>{customSuffixIcon}</IconWrapper> : null}
    </TitleWrapper>
  )
);

export { titleIconMap, sizeMap, colorMap, fontWeightMap };
export type { FontSize, TextColor, FontWeightType };
export default Title;
